import ReactGA from 'react-ga4';

interface GAEventParams {
  action: string;
  category: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

class GoogleAnalyticsService {
  private isInitialized = false;

  /**
   * Inicializa o Google Analytics com o ID do ambiente
   */
  initialize() {
    const gaId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
    
    if (!gaId) {
      console.warn('GA4 Measurement ID não encontrado nas variáveis de ambiente');
      return;
    }

    if (this.isInitialized) {
      console.warn('Google Analytics já foi inicializado');
      return;
    }

    try {
      ReactGA.initialize(gaId, {
        testMode: process.env.NODE_ENV === 'development',
        gtagOptions: {
          debug_mode: process.env.NODE_ENV === 'development',
        },
      });
      
      this.isInitialized = true;
      console.log('Google Analytics inicializado com sucesso:', gaId);
    } catch (error) {
      console.error('Erro ao inicializar Google Analytics:', error);
    }
  }

  /**
   * Envia um page view para o GA
   */
  sendPageView(path?: string) {
    if (!this.isInitialized) {
      console.warn('Google Analytics não foi inicializado');
      return;
    }

    try {
      ReactGA.send({ 
        hitType: "pageview", 
        page: path || window.location.pathname + window.location.search 
      });
    } catch (error) {
      console.error('Erro ao enviar page view:', error);
    }
  }

  /**
   * Envia um evento customizado para o GA
   */
  sendEvent({ action, category, label, value, custom_parameters }: GAEventParams) {
    if (!this.isInitialized) {
      console.warn('Google Analytics não foi inicializado');
      return;
    }

    try {
      ReactGA.event(action, {
        event_category: category,
        event_label: label,
        value: value,
        ...custom_parameters,
      });
    } catch (error) {
      console.error('Erro ao enviar evento:', error);
    }
  }

  /**
   * Eventos pré-definidos para facilitar o uso
   */
  events = {
    // Eventos de navegação
    pageView: (page: string) => {
      this.sendPageView(page);
    },

    // Eventos de interação com botões
    buttonClick: (buttonName: string, location?: string) => {
      this.sendEvent({
        action: 'click',
        category: 'button',
        label: buttonName,
        custom_parameters: { location }
      });
    },

    // Eventos de formulário
    formSubmit: (formName: string, success: boolean = true) => {
      this.sendEvent({
        action: success ? 'submit_success' : 'submit_error',
        category: 'form',
        label: formName
      });
    },

    formStart: (formName: string) => {
      this.sendEvent({
        action: 'start',
        category: 'form',
        label: formName
      });
    },

    // Eventos de login/logout
    login: (method?: string) => {
      this.sendEvent({
        action: 'login',
        category: 'auth',
        label: method || 'default'
      });
    },

    logout: () => {
      this.sendEvent({
        action: 'logout',
        category: 'auth'
      });
    },

    // Eventos de erro
    error: (errorType: string, errorMessage?: string) => {
      this.sendEvent({
        action: 'error',
        category: 'system',
        label: errorType,
        custom_parameters: { error_message: errorMessage }
      });
    },

    // Eventos de busca
    search: (searchTerm: string, resultsCount?: number) => {
      this.sendEvent({
        action: 'search',
        category: 'engagement',
        label: searchTerm,
        value: resultsCount
      });
    },

    // Eventos de download
    download: (fileName: string, fileType?: string) => {
      this.sendEvent({
        action: 'download',
        category: 'file',
        label: fileName,
        custom_parameters: { file_type: fileType }
      });
    },

    // Eventos de compartilhamento
    share: (contentType: string, method?: string) => {
      this.sendEvent({
        action: 'share',
        category: 'engagement',
        label: contentType,
        custom_parameters: { method }
      });
    }
  };

  /**
   * Verifica se o GA está inicializado
   */
  get initialized() {
    return this.isInitialized;
  }
}

// Exporta uma instância única (singleton)
export const GA = new GoogleAnalyticsService();
export default GA;
